import { useEffect, useState } from "react";

/** Resolves a stable DOM node (e.g. `#top-nav-subheader`) for `createPortal`. */
export function usePortalTarget(id: string): HTMLElement | null {
  const [target, setTarget] = useState<HTMLElement | null>(() =>
    typeof document !== "undefined" ? document.getElementById(id) : null,
  );

  useEffect(() => {
    if (typeof document === "undefined") return;

    const syncTarget = () => {
      const element = document.getElementById(id);
      setTarget((current) => (current === element ? current : element));
      return Boolean(element);
    };

    if (syncTarget()) {
      return;
    }

    const frame = requestAnimationFrame(syncTarget);
    const observer = new MutationObserver(() => {
      if (syncTarget()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [id]);

  return target;
}
